<template>
    <div id="myChartBookTable">
        <div class="ml-auto text-right mb-1 print:hidden" v-if="!editMode">
            <button class="items-center text-xs lg:text-sm btn btn-sm text-blue-600 hover:text-blue-400 mb-2 mt-2" @click="flip">
                <span class="mr-1">FlipCharts</span> <i class="fas fa-chart-area"></i>
            </button>
        </div>
        <div class="border-t-2 border-blue-600">
            <div class="table-header border-b">
                <div class="flex flex-wrap w-full">
                    <div class="relative w-full sm:w-auto pr-3 xs:pr-0 xs:pb-2 font-bold mt-1">
                        <span class="w-full">
                            <span class="inline-block mr-1">Chart Book:</span>
                            <template v-if="editMode">
                                <input :class="{'is-invalid': chartbookNameError}" class="form-input-inline-w-none form-input-sm-inline" type="text" v-model="chartbook.name" maxlength="40" placeholder="Enter a chartbook name..." />
                            </template>
                            <template v-else>{{ chartbook.name }}</template>
                        </span>
                    </div>

                    <div v-if="editMode" class="flex flex-wrap w-full lg:w-auto mt-3 ml-auto sm:mt-2 lg:mt-0 border-t border-gray-400 lg:border-t-0">
                        <div @click="cancel" class="inline-block ml-auto mr-0 sm:mr-3 xs:w-full mt-2 xs:mb-2 lg:my-0">
                            <div class="btn btn-sm btn-yellow-ghost">Cancel</div>
                        </div>
                        <div @click="saveChartbook" class="inline-block xs:w-full xs:mb-2 xs:text-right sm:mt-2 mb-1 lg:my-0 xs:mx-auto xs:pt-3 xs:border-t">
                            <div class="btn btn-sm btn-yellow">Save</div>
                        </div>
                    </div>
                    <div v-else class="flex flex-wrap w-full lg:w-auto mt-3 ml-auto sm:mt-2 lg:mt-0 border-t border-gray-400 lg:border-t-0">
                        <div class="inline-block ml-auto mr-0 sm:mr-3 xs:w-full mt-2 xs:mb-2 lg:my-0" v-if="(jims && isJim()) || !jims">
                            <label>
                                <select v-model="newChartSection" class="form-control form-control-sm form-control-fixed form-control-blue-600" aria-label="Add a Chart">
                                    <option :value="''">Add a Chart</option>
                                    <option value="futures">Future</option>
                                    <option value="forex">Forex</option>
                                    <option value="future-spreads">Future Spreads</option>
                                    <option value="future-options">Options</option>
                                    <option value="crypto">Crypto</option>
                                    <option value="stock-etf">Stocks</option>
                                </select>
                            </label>
                        </div>
                        <div v-if="!jims || (jims && isJim())" @click="editThisBook" class="inline-block xs:w-full xs:mb-2 xs:text-right sm:mt-2 mb-1 lg:my-0 xs:mx-auto xs:pt-3 xs:border-t">
                            <div class="btn btn-sm btn-yellow"><i class="far fa-edit"></i>&nbsp;Edit This Book</div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="charts.length === 0">
                <alert-box>
                    <template #body>
                        No charts have been added to this chart book.
                    </template>
                </alert-box>
            </div>

            <div class="table-responsive" v-if="charts.length > 0">
                <table class="table border border-t-0 table-striped table-hover">
                    <thead>
                        <tr>
                            <th v-if="editMode && !jims" scope="col" class="w-16 text-center">Sort</th>
                            <th scope="col" @click="changeSort('symbol')" class="sortable w-1/3" :class="{'w-1/5': !jims || (jims && isJim())}">Symbol</th>
                            <th scope="col" @click="changeSort('contractFirstNoticeDate')" class="sortable">First Notice</th>
                            <th scope="col" @click="changeSort('contractExpirationDate')" class="sortable">Expiration Date</th>
                            <th scope="col" @click="changeSort('title')" class="sortable">Notes</th>
                            <th scope="col" @click="changeSort('updated_at')" class="sortable">Last Updated</th>
                            <th v-if="isJim()" class="w1-16 text-center">Public</th>
                            <th v-if="!jims || (jims && isJim())" class="text-center w-16">Actions</th>
                        </tr>
                    </thead>

                    <draggable v-bind="draggableOptions" v-model="charts" tag="tbody" item-key="id">
                        <template #item="{ element, index }">
                            <tr>
                                <td v-if="editMode && !jims" class="text-center cursor-move canDrag"><i class="fas fa-arrows-alt text-gray-500"></i></td>
                                <td><a :href="getChartLink(element)">{{ element.symbol_display || element?.symbol }}</a></td>
                                <td>{{ quotesStore[element.symbol]?.contractFirstNoticeDate ?? '' }}</td>
                                <td>{{ quotesStore[element.symbol]?.contractExpirationDate ?? '' }}</td>
                                <td>{{ element.notes || '-' }}</td>
                                <td nowrap>{{ element.updated_at }}</td>
                                <td class="text-center" v-if="isJim()">
                                    <span v-if="!editMode">{{ element.public ? 'Yes' : 'No' }}</span>
                                    <input type="checkbox" :value="true" v-model="element.public" v-else>
                                </td>
                                <td v-if="!jims || (jims && isJim())" class="text-center">
                                    <div class="inline-flex">
                                        <span v-if="!editMode" @click="showUpdateNoteModal(element)" class="btn btn-default btn-shadow-none btn-blue-ghost btn-xs mb-1 md:mb-0" title="Update Notes"><i class="far fa-edit"></i></span>
                                        <span v-if="!editMode && chartbooks.length > 1" @click="showMoveModal(element)" class="btn btn-default btn-shadow-none btn-blue-ghost btn-xs ml-1 mb-1 md:mb-0" title="Move Chart"><i class="fas fa-exchange-alt"></i></span>
                                        <span v-if="editMode" @click="deleteChart(index)" class="btn btn-default btn-shadow-none btn-red-ghost btn-xs ml-1 mb-1 md:mb-0" title="Delete Chart"><i class="fas fa-times"></i></span>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </draggable>

                </table>
            </div>
            <div v-if="editMode" class="flex justify-center mt-4">
                <div @click="cancel" class="btn btn-sm btn-yellow-ghost inline-flex mx-1 xs:ml-0">Cancel</div>
                <div @click="saveChartbook" class="btn btn-sm btn-yellow inline-flex mx-1">Save</div>
            </div>
        </div>

        <flash-message ref="flash"></flash-message>

        <add-new-chart-modal @added="addToChartbook" :chartbook="chartbook" :existing-symbols="existingChartSymbols" @close="closeAddNewChartModal" :section="newChartSection" v-if="modal === 'add-new-chart-modal'">
        </add-new-chart-modal>

        <delete-chart-modal :chart="selectedChart" @delete="deleteChart" @close="modal = null" v-if="modal === 'delete-chart-modal'">
        </delete-chart-modal>

        <update-chart-modal @update="updateChart" :chart="selectedChart" :symbolDisplay="'Oats Dec 2019'" :chartBooks="chartbooks" @close="modal = null" v-if="modal === 'update-chart-modal'">
        </update-chart-modal>

        <move-chart-modal @move="moveChart" :chart="selectedChart" :symbolDisplay="'Oats Dec 2019'" :chartBooks="chartbooks" @close="modal = null" v-if="modal === 'move-chart-modal'">
        </move-chart-modal>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import Draggable from 'vuedraggable';
import { useFlipcharts } from '../../DataTable/mixins/FlipCharts';
import { useIsJim } from '../../../mixins/IsJim';

import AddNewChartModal from './Modals/AddNewChartModal';
import DeleteChartModal from './Modals/DeleteChartModal';
import UpdateChartModal from './Modals/UpdateChartModal';
import MoveChartModal from './Modals/MoveChartModal';

// Props
const props = defineProps({
    jims: Boolean,
    initialChartbook: Object,
    initialChartbooks: Array
});

const emit = defineEmits(['editMode', 'movedChart', 'updatedChart', 'saved']);

const flash = ref(null);
const chartbook = ref(JSON.parse(JSON.stringify(props.initialChartbook)));
const chartbooks = ref(JSON.parse(JSON.stringify(props.initialChartbooks)));

const oldChartBook = ref({});
const oldChartBooks = ref({});
const oldCharts = ref({});

const charts = ref([...props.initialChartbook.charts]);

const chartbookName = ref(props.initialChartbook.name);
const selectedChart = ref(null);
const sortField = ref('');
const sortDirection = ref('asc');

const newChartSection = ref('');

const modal = ref(null);
const editMode = ref(false);
const chartbookNameError = ref(false);

const quotesStore = ref({});

const { isJim } = useIsJim();
const { flipcharts } = useFlipcharts();

const draggableOptions = computed(() => {
    return {
        disabled: props.jims ? true : false,
        handle: '.canDrag'
    };
});

const existingChartSymbols = computed(() => {
    return chartbooks.value.reduce((obj, book) => {
        book.charts.forEach(chart => {
            obj[chart.symbol] = book.name;
        });
        return obj;
    }, {});
});

watch(() => props.initialChartbook, (newValue) => {
    chartbook.value = JSON.parse(JSON.stringify(newValue));
    // charts.value = JSON.parse(JSON.stringify(newValue.charts));
    charts.value = [...newValue.charts];
    chartbookName.value = newValue.name;
    getQuotes(); // in mounted?
}, { deep: true });

watch(newChartSection, (newValue) => {
    if (newValue !== '') {
        modal.value = 'add-new-chart-modal';
    }
});

// Methods
function flip() {
    const chartIds = charts.value.map(c => c.id);
    flipcharts(null, chartIds);
}

function editThisBook() {
    emit('editMode', true);
    chartbookNameError.value = null;
    oldCharts.value = JSON.parse(JSON.stringify(charts.value));
    oldChartBook.value = JSON.parse(JSON.stringify(chartbook.value));
    oldChartBooks.value = JSON.parse(JSON.stringify(chartbooks.value));
    editMode.value = true;
}

function cancel() {
    emit('editMode', false);
    chartbookNameError.value = null;
    charts.value = JSON.parse(JSON.stringify(oldCharts.value));
    chartbook.value = JSON.parse(JSON.stringify(oldChartBook.value));
    chartbooks.value = JSON.parse(JSON.stringify(oldChartBooks.value));
    editMode.value = false;
}

function getChartLink(chart) {
    let routeName;

    if (props.jims) {
        routeName = 'jims-desk.chart-book.view-jims';
        return window.route(routeName, { 'chart_book': chartbook.value.id, chart: chart.id });
    } else {
        routeName = 'interactive-charts.home';
        return window.route(routeName, { symbol: chart.symbol });
    }
}

function addToChartbook(chart) {
    charts.value.push(chart);
    saveChartbook();
}

function moveChart(chart) {
    charts.value = charts.value.filter(c => c.id !== chart.id);
    emit('movedChart', chartbook.value.id, chart.interactive_chart_book_id, chart);
}

function updateChart(chart) {
    emit('updatedChart', chart.interactive_chart_book_id, chart);
}
function deleteChart(index) {
    charts.value.splice(index, 1);
    // emit('removedChart', chart.value.interactive_chart_book_id, chart.value);
}

function saveChartbook() {
    // validate chartbook name
    if (!chartbook.value.name) {
        flash.value.error('Chartbook name cannot be empty');
        chartbookNameError.value = true;
        return;
    } else {
        chartbookNameError.value = null;
    }

    const route = window.route('my-folder.chart-book.update', chartbook.value);

    axios.put(route, { chartbook: chartbook.value, charts: charts.value })
        .then((response) => {
            flash.value.display('Updated chartbook');
            emit('saved', response.data.chartbook);
            emit('editMode', false);
        }).catch((error) => {
            flash.value.error('Error updating chartbook');
        });

    editMode.value = false;
}

function changeSort(field) {
    if (field === sortField.value) {
        sortDirection.value = sortDirection.value === 'desc' ? 'asc' : 'desc';
    }

    const lessThan = sortDirection.value === 'asc' ? -1 : 1;
    const greaterThan = sortDirection.value === 'asc' ? 1 : -1;

    sortField.value = field;

    if (['contractFirstNoticeDate', 'contractExpirationDate'].includes(field)) {
        return sortByQuotes(field);
    }

    charts.value.sort((a, b) => {
        if (a[field] < b[field]) return lessThan;
        if (a[field] > b[field]) return greaterThan;
        return 0;
    });
}

function sortByQuotes(field) {
    const lessThan = sortDirection.value === 'asc' ? -1 : 1;
    const greaterThan = sortDirection.value === 'asc' ? 1 : -1;

    charts.value.sort((a, b) => {
        const first = quotesStore.value[a.symbol] || { raw: 0 };
        const second = quotesStore.value[b.symbol] || { raw: 0 };
        if (first[field] < second[field]) return lessThan;
        if (first[field] > second[field]) return greaterThan;
        return 0;
    });
}

function getQuotes() {
    const route = window.route('api.quotes');
    const chartBookSymbols = chartbook.value.charts.map(c => c.symbol);
    const symbols = chartBookSymbols.filter((mSymbol) => quotesStore.value[mSymbol] === undefined);

    const params = {
        symbols: symbols.join(),
        fields: 'symbol,contractFirstNoticeDate,contractExpirationDate'
    };

    axios.post(route, params)
        .then((response) => {
            response.data.data.map(row => {
                quotesStore.value[row.symbol] = row;
            });
        }).catch(error => {
        });
}

function showUpdateNoteModal(chart) {
    selectedChart.value = chart;
    modal.value = 'update-chart-modal';
}

function showMoveModal(chart) {
    selectedChart.value = chart;
    modal.value = 'move-chart-modal';
}

function removeChart(chart) {
    selectedChart.value = chart;
    modal.value = 'delete-chart-modal';
}

function closeAddNewChartModal() {
    modal.value = null;
    newChartSection.value = '';
}

onMounted(() => {
    changeSort('sort_order');

    // Force reload when using back button
    if (performance.navigation.type === 2) {
        location.reload(true);
    }

    getQuotes();
});
</script>

<style lang="scss">
#myChartBookTable {
    table {
        thead {
            td, th {
                @apply px-3 py-2;
            }
        }
        tbody {
            td, th {
                @apply px-3 py-1 text-sm;
            }
        }
        .btn i {
            width: 12px;
        }
        .sortable {
            @apply cursor-pointer;
        }
        .expanded-row-01 td {
            @apply border-b-0;
        }
        .expanded-row-02 td {
            @apply border-t-0;
        }
    }
}
.myChartBookModal {
    @apply text-left;
    .modal-container {
        width: 28rem;
        @screen xs {
            width: 100%;
        }
    }
    textarea {
        @apply text-sm;
    }
    sup {
        @apply text-red-500;
    }
}
</style>